/**
 * 基本設計の成果物 API クライアント。
 *
 * 要件定義（`spec-requirements.ts`）とは別の流れなので、型も口も共有しない。要件定義側が
 * 型を変えても、基本設計の画面が黙って壊れないようにする。
 *
 * 節の名前・列・選択肢・上限は API（`GET /basic-design/artifacts`）が返す定義を使う。
 * 画面に写すと、domain で節や選択肢が増えたときに黙ってずれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の定義・成果物・次の 1 手・図・書き出し・履歴・逆引き・レビュー・メモを読み、節とメモを保存しレビューを依頼・提出する API クライアント
 * @graph-connects /basic-design [calls] 基本設計 API
 */

import { request } from './_request.js';

/** API に渡す言語。 @graph-connects none */
export type BdLang = 'en' | 'ja';

/**
 * 画面の言語から API の言語を決める。`ja` で始まれば日本語、それ以外は英語（API の既定）。
 * @graph-connects none
 */
export function bdLangOf(language: string | undefined): BdLang {
  return language?.toLowerCase().startsWith('ja') === true ? 'ja' : 'en';
}

/**
 * 節の名前（`impact_list` など）。取りうる値を型に写すと domain で増えたときに黙ってずれるので
 * `string` のままにする。
 * @graph-connects none
 */
export type BdArtifact = string;

/** 1 行。値はすべて文字列で、空文字は未記入。 @graph-connects none */
export type BdRow = Record<string, string>;

/** 列の種類。 @graph-connects none */
export type BdColumnKind = 'id' | 'text' | 'choice' | 'ref' | 'refs' | 'flowNodes';

/** 選択肢 1 つ。`value` を保存し、`label` を見せる。 @graph-connects none */
export interface BdChoice {
  value: string;
  label: string;
}

/** 言語を決めた列の定義。 @graph-connects none */
export interface BdColumnDef {
  key: string;
  kind: BdColumnKind;
  label: string;
  required: boolean;
  limit?: number;
  choices?: BdChoice[];
  /** 参照先の節（ref・refs） */
  refTo?: BdArtifact[];
  hint?: string;
  placeholder?: string;
  width: 'full' | 'half' | 'quarter';
}

/** 言語を決めた節の定義。 @graph-connects none */
export interface BdArtifactDef {
  key: BdArtifact;
  /** テンプレートの節番号（`1a`・`1c-ii`・`Step 3` など） */
  section: string;
  title: string;
  summary: string;
  requirement: 'required' | 'optional';
  /** 行 ID の接頭辞。行 ID を持たない節は null */
  idPrefix: string | null;
  rowLimit: number;
  columns: BdColumnDef[];
  step: { action: string; reason: string };
}

/** 一覧に出す節の版。行は持たない。 @graph-connects none */
export interface BdDocumentMeta {
  version: number;
  updatedAt: string;
  updatedBy: string;
  rowCount: number;
}

/** 完了までの進み。 @graph-connects none */
export interface BdProgress {
  saved: number;
  total: number;
  requiredSaved: number;
  requiredTotal: number;
  done: boolean;
}

/** 一覧に出す基本設計 1 件。 @graph-connects none */
export interface BdSummary {
  bdKey: string;
  /** 親チケット。親の無いチケットでは bdKey と同じ */
  rootKey: string;
  bdTitle: string;
  /** 保存した節だけ */
  artifacts: Partial<Record<BdArtifact, BdDocumentMeta>>;
  progress: BdProgress;
  /** 節のうち最後に保存した日時（ISO 8601） */
  updatedAt: string;
  /** レビューの要約。まだ依頼していなければ null */
  review: BdReviewSummary | null;
}

/** 保存済みの節 1 つ。 @graph-connects none */
export interface BdSavedArtifact {
  rows: BdRow[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** 基本設計 1 件の成果物。 @graph-connects none */
export interface BdDetail {
  bdKey: string;
  rootKey: string;
  bdTitle: string;
  /** 同じ親チケットの基本設計（自分も含む。節をまだ保存していないチケットは入らない） */
  siblings: BdSummary[];
  /** 保存した節だけ */
  artifacts: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** レビューの要約。まだ依頼していなければ null */
  review: BdReviewSummary | null;
}

/**
 * 基本設計の次の 1 手。
 *
 * `runbook` は MCP のツール呼び出しの列なので、画面では「Claude に頼むときの手順」として出す。
 * @graph-connects none
 */
export interface BdStep {
  /**
   * `write_<節>`・`request_review`・`ai_review`・`resolve_findings`・`reviewer_review`・`done` の
   * どれか。レビューの段階が増えても画面が壊れないよう `string` のまま受ける。
   */
  id: string;
  artifact: BdArtifact | null;
  phase: string;
  action: string;
  reason: string;
  runbook: string[];
  /** ほかに書いていない節の名前 */
  pending: string[];
}

/** 節のフォーマット文書。 @graph-connects none */
export interface BdFormat {
  artifact: BdArtifact;
  markdown: string;
}

/** チェックの指摘 1 つ。`row` は送った行の並びでの位置（0 から）。 @graph-connects none */
export interface BdIssue {
  severity: 'error' | 'warning';
  code: string;
  row?: number;
  column?: string;
  params?: Record<string, string | number>;
  /** 行・列の前置きを付けない文 */
  message: string;
  /** 行・列の前置きを付けた文 */
  text: string;
}

/** チェックの結果。 @graph-connects none */
export interface BdCheckResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
  issues: BdIssue[];
}

/** 保存の結果。 @graph-connects none */
export interface BdSaveResult {
  bdKey: string;
  artifact: BdArtifact;
  version: number;
  created: boolean;
  warnings: string[];
  issues: BdIssue[];
}

/** 全体図と ER 図の Mermaid。描くものが無ければ null。 @graph-connects none */
export interface BdDiagrams {
  overview: string | null;
  erd: string | null;
}

/** 基本設計 1 件の Markdown 書き出し。 @graph-connects none */
export interface BdDocumentExport {
  markdown: string;
}

/** 履歴の 1 版（行は持たない）。 @graph-connects none */
export interface BdRevisionMeta {
  version: number;
  savedAt: string;
  savedBy: string;
  rowCount: number;
}

/** 履歴の 1 版（行つき）。 @graph-connects none */
export interface BdRevision {
  version: number;
  savedAt: string;
  savedBy: string;
  rows: BdRow[];
}

/** 逆引きで見つかった行。 @graph-connects none */
export interface BdTargetRow {
  id: string;
  impact: string;
  decisions: { id: string; topic: string; chosen: string; status: string }[];
}

/** 逆引きの結果。 @graph-connects none */
export interface BdTargets {
  key: string;
  items: {
    bdKey: string;
    rootKey: string;
    bdTitle: string;
    artifact: BdArtifact;
    rows: BdTargetRow[];
  }[];
}

/** 図のノードの種類。 @graph-connects none */
export type BdGraphNodeKind =
  | 'flow'
  | 'screen'
  | 'api'
  | 'event'
  | 'service'
  | 'domain'
  | 'entity'
  | 'decision';

/** 動く図のノード 1 つ。 @graph-connects none */
export interface BdGraphNode {
  id: string;
  kind: BdGraphNodeKind;
  /** 左から何列目か（0 始まり） */
  layer: number;
  label: string;
  /** 札の 2 行目の短い説明 */
  sub?: string;
  /** 変更の種類の値（new・modified・removed・unchanged・reused） */
  impact?: string;
  /** 業務フローのノードへの影響の向き（影響一覧の direction の値） */
  direction?: string;
  /** このノードの元の行 */
  ref?: { artifact: BdArtifact; rowId: string };
}

/** 動く図の線 1 本。 @graph-connects none */
export interface BdGraphEdge {
  id: string;
  source: string;
  target: string;
  kind: string;
  label?: string;
}

/** 動く図 1 枚。 @graph-connects none */
export interface BdGraph {
  nodes: BdGraphNode[];
  edges: BdGraphEdge[];
}

/** 全体・ER・画面遷移の 3 枚の動く図。 @graph-connects none */
export interface BdGraphs {
  overview: BdGraph;
  erd: BdGraph;
  transitions: BdGraph;
}

/** レビューの状態。 @graph-connects none */
export type BdReviewStatus =
  | 'requested'
  | 'ai_changes_requested'
  | 'ai_approved'
  | 'reviewer_changes_requested'
  | 'approved';

/** 段階を出す役。 @graph-connects none */
export type BdReviewRole = 'ai' | 'reviewer';

/** 指摘の重さ。 @graph-connects none */
export type BdFindingSeverity = 'critical' | 'major' | 'minor';

/** 指摘の扱い。 @graph-connects none */
export type BdFindingResolution = 'open' | 'fixed' | 'dismissed' | 'withdrawn';

/** 指摘の解決の指定。`reopen` は未解決に戻す。 @graph-connects none */
export type BdFindingAction = 'fixed' | 'dismissed' | 'withdrawn' | 'reopen';

/** チェックリストの答え。 @graph-connects none */
export type BdChecklistResult = 'ok' | 'ng' | 'na';

/** 指摘 1 件。 @graph-connects none */
export interface BdReviewFinding {
  id: string;
  perspective: string;
  severity: BdFindingSeverity;
  summary: string;
  /** 指摘が指す箇所（`api_contracts A2` など）。無ければ空文字 */
  target: string;
  foundInRound: number;
  by: BdReviewRole;
  resolution: BdFindingResolution;
  resolutionNote: string;
}

/** チェックリストの 1 観点。 @graph-connects none */
export interface BdChecklistEntry {
  perspective: string;
  result: BdChecklistResult;
  evidence: string;
  by: BdReviewRole;
  /** ISO 8601 */
  at: string;
}

/** 承認の記録。 @graph-connects none */
export interface BdReviewApproval {
  role: BdReviewRole;
  actor: string;
  round: number;
  /** ISO 8601 */
  at: string;
}

/** レビューの観点 1 つ（言語を決めたもの）。 @graph-connects none */
export interface BdReviewPerspective {
  key: string;
  label: string;
  /** 何を確かめるか（1 文） */
  check: string;
  /** 見る節 */
  artifacts: { key: BdArtifact; title: string }[];
}

/**
 * 基本設計のレビュー。まだ依頼していないときは `status` が null で、観点だけが入る。
 * @graph-connects none
 */
export interface BdReview {
  status: BdReviewStatus | null;
  /** 依頼した後に中身が変わった（依頼し直しが要る） */
  stale: boolean;
  round: number;
  reviewer: { nickName: string } | null;
  requestedBy: string;
  findings: BdReviewFinding[];
  checklist: BdChecklistEntry[];
  approvals: BdReviewApproval[];
  perspectives: BdReviewPerspective[];
  /** 呼んだ人が指名されたレビュアーか（判定は API がメールで行う） */
  callerIsReviewer: boolean;
}

/**
 * API が返すレビューの形。観点は依頼の前から要るので、レビュー本体の外に並べて返ってくる。
 * @graph-connects none
 */
interface BdReviewResponse {
  bdKey: string;
  perspectives: BdReviewPerspective[];
  review: Omit<BdReview, 'perspectives'> | null;
}

/**
 * 画面が使う 1 つの形にそろえる。依頼の前は本体が null なので、空のレビューに観点だけを載せる。
 * @graph-connects none
 */
export function toBdReview(response: BdReviewResponse): BdReview {
  if (response.review === null) {
    return {
      status: null,
      stale: false,
      round: 0,
      reviewer: null,
      requestedBy: '',
      findings: [],
      checklist: [],
      approvals: [],
      perspectives: response.perspectives,
      callerIsReviewer: false,
    };
  }
  return { ...response.review, perspectives: response.perspectives };
}

/** 一覧・詳細に出すレビューの要約。 @graph-connects none */
export interface BdReviewSummary {
  status: BdReviewStatus;
  stale: boolean;
  round: number;
  reviewerNickName: string;
  openFindings: number;
  /** 承認日時（ISO 8601）。承認済みで古くないときだけ */
  approvedAt?: string;
}

/** 提出する指摘 1 件。 @graph-connects none */
export interface BdFindingInput {
  perspective: string;
  severity: BdFindingSeverity;
  summary: string;
  target: string;
}

/** 提出するチェックリストの 1 観点。 @graph-connects none */
export interface BdChecklistInput {
  perspective: string;
  result: BdChecklistResult;
  evidence: string;
}

/** 段階の提出。 @graph-connects none */
export interface BdReviewSubmitBody {
  role: BdReviewRole;
  findings: BdFindingInput[];
  checklist?: BdChecklistInput[];
}

/** 指摘の解決 1 件。 @graph-connects none */
export interface BdFindingActionInput {
  id: string;
  action: BdFindingAction;
  note?: string;
}

/** 共有のメモ。まだ保存していなければ markdown は空文字、版は 0、日時と保存した人は null。 @graph-connects none */
export interface BdNotes {
  bdKey: string;
  markdown: string;
  version: number;
  /** ISO 8601 */
  updatedAt: string | null;
  updatedBy: string | null;
}

/**
 * 共有のメモの保存の結果。本文は返らないので、画面はメモを読み直して出す。
 * @graph-connects none
 */
export interface BdNotesSaveResult {
  bdKey: string;
  version: number;
  created: boolean;
}

/** bdKey の下のパスを組み立てる。 @graph-connects none */
function bdPath(bdKey: string, rest = ''): string {
  return `/basic-design/${encodeURIComponent(bdKey)}${rest}`;
}

/** 節の下のパスを組み立てる。 @graph-connects none */
function artifactPath(bdKey: string, artifact: BdArtifact, rest = ''): string {
  return bdPath(bdKey, `/artifacts/${encodeURIComponent(artifact)}${rest}`);
}

/** `?lang=` を付ける。 @graph-connects none */
function withLang(path: string, lang: BdLang): string {
  return `${path}${path.includes('?') ? '&' : '?'}lang=${lang}`;
}

/** @graph-connects /basic-design [calls] 基本設計 API */
export const basicDesignApi = {
  /** @graph-connects /basic-design [calls] 節の定義 */
  artifacts: (lang: BdLang): Promise<{ items: BdArtifactDef[] }> =>
    request(withLang('/basic-design/artifacts', lang)),
  /** @graph-connects /basic-design [calls] 節のフォーマット */
  format: (artifact: BdArtifact, lang: BdLang): Promise<BdFormat> =>
    request(withLang(`/basic-design/formats/${encodeURIComponent(artifact)}`, lang)),
  /** @graph-connects /basic-design [calls] API・エンティティ・画面からの逆引き */
  targets: (key: string, lang: BdLang): Promise<BdTargets> =>
    request(withLang(`/basic-design/targets?key=${encodeURIComponent(key)}`, lang)),
  /** @graph-connects /basic-design [calls] 基本設計の一覧 */
  list: (lang: BdLang): Promise<{ items: BdSummary[] }> => request(withLang('/basic-design', lang)),
  /** @graph-connects /basic-design [calls] 基本設計 1 件の成果物 */
  detail: (bdKey: string): Promise<BdDetail> => request(bdPath(bdKey)),
  /** @graph-connects /basic-design [calls] 次の 1 手 */
  nextStep: (bdKey: string, lang: BdLang): Promise<BdStep> =>
    request(withLang(bdPath(bdKey, '/next-step'), lang)),
  /** @graph-connects /basic-design [calls] 全体図と ER 図 */
  diagrams: (bdKey: string, lang: BdLang): Promise<BdDiagrams> =>
    request(withLang(bdPath(bdKey, '/diagrams'), lang)),
  /** @graph-connects /basic-design [calls] Markdown の書き出し */
  document: (bdKey: string, lang: BdLang): Promise<BdDocumentExport> =>
    request(withLang(bdPath(bdKey, '/document'), lang)),
  /** @graph-connects /basic-design [calls] 節を保存せずに確かめる */
  check: (
    bdKey: string,
    artifact: BdArtifact,
    rows: BdRow[],
    lang: BdLang,
  ): Promise<BdCheckResult> =>
    request(withLang(bdPath(bdKey, `/check/${encodeURIComponent(artifact)}`), lang), {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  /** @graph-connects /basic-design [calls] 節の保存 */
  save: (bdKey: string, artifact: BdArtifact, rows: BdRow[], lang: BdLang): Promise<BdSaveResult> =>
    request(withLang(artifactPath(bdKey, artifact), lang), {
      method: 'PUT',
      body: JSON.stringify({ rows }),
    }),
  /** @graph-connects /basic-design [calls] 節の履歴 */
  revisions: (bdKey: string, artifact: BdArtifact): Promise<{ items: BdRevisionMeta[] }> =>
    request(artifactPath(bdKey, artifact, '/revisions')),
  /** @graph-connects /basic-design [calls] 全体・ER・画面遷移の動く図 */
  graph: (bdKey: string, lang: BdLang): Promise<BdGraphs> =>
    request(withLang(bdPath(bdKey, '/graph'), lang)),
  /** @graph-connects /basic-design [calls] レビュー */
  review: async (bdKey: string, lang: BdLang): Promise<BdReview> =>
    toBdReview(await request<BdReviewResponse>(withLang(bdPath(bdKey, '/review'), lang))),
  /** @graph-connects /basic-design [calls] レビュアーの候補（ニックネームだけ） */
  reviewers: (query: string): Promise<{ items: { nickName: string }[] }> =>
    request(`/basic-design/reviewers?q=${encodeURIComponent(query)}`),
  /** @graph-connects /basic-design [calls] レビューの依頼 */
  requestReview: async (bdKey: string, reviewer: string, lang: BdLang): Promise<BdReview> =>
    toBdReview(
      await request<BdReviewResponse>(withLang(bdPath(bdKey, '/review/request'), lang), {
        method: 'POST',
        body: JSON.stringify({ reviewer }),
      }),
    ),
  /** @graph-connects /basic-design [calls] レビューの段階の提出 */
  submitReview: async (bdKey: string, body: BdReviewSubmitBody, lang: BdLang): Promise<BdReview> =>
    toBdReview(
      await request<BdReviewResponse>(withLang(bdPath(bdKey, '/review/submit'), lang), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    ),
  /** @graph-connects /basic-design [calls] 指摘の解決 */
  resolveFindings: async (
    bdKey: string,
    items: BdFindingActionInput[],
    lang: BdLang,
  ): Promise<BdReview> =>
    toBdReview(
      await request<BdReviewResponse>(withLang(bdPath(bdKey, '/review/findings'), lang), {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),
    ),
  /** @graph-connects /basic-design [calls] 共有のメモ */
  notes: (bdKey: string): Promise<BdNotes> => request(bdPath(bdKey, '/notes')),
  /** @graph-connects /basic-design [calls] 共有のメモの保存 */
  saveNotes: (bdKey: string, markdown: string): Promise<BdNotesSaveResult> =>
    request(bdPath(bdKey, '/notes'), { method: 'PUT', body: JSON.stringify({ markdown }) }),
  /** @graph-connects /basic-design [calls] 節の過去の 1 版 */
  revision: (bdKey: string, artifact: BdArtifact, version: number): Promise<BdRevision> =>
    request(artifactPath(bdKey, artifact, `/revisions/${String(version)}`)),
};
